import { formatAttachmentSize, resolveMediaUrl } from "../components/messaging/messagingUi";

export const REVIEW_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const REVIEW_MAX_MEDIA_FILES = 6;
export const REVIEW_MAX_VIDEO_FILES = 2;
export const REVIEW_MEDIA_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/webm,video/quicktime,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.mp4,.webm,.mov";

const REVIEW_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
]);

const REVIEW_VIDEO_TYPES = new Set([
  "video/mp4",
  "video/webm",
  "video/quicktime",
]);

const REVIEW_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"]);
const REVIEW_VIDEO_EXTENSIONS = new Set(["mp4", "webm", "mov"]);

function getFileExtension(value) {
  const normalized = String(value || "").trim().toLowerCase().split(/[?#]/)[0];
  const parts = normalized.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
}

function inferReviewMediaType(value, fileName = "") {
  const normalized = String(value || "").trim().toLowerCase();
  if (REVIEW_IMAGE_TYPES.has(normalized) || REVIEW_IMAGE_EXTENSIONS.has(getFileExtension(fileName || value))) {
    return "image";
  }
  if (REVIEW_VIDEO_TYPES.has(normalized) || REVIEW_VIDEO_EXTENSIONS.has(getFileExtension(fileName || value))) {
    return "video";
  }
  return null;
}

function normalizeMediaEntry(entry, fallbackIndex = 0) {
  const rawUrl = typeof entry === "string"
    ? entry
    : entry?.url || entry?.path || entry?.src || entry?.attachmentUrl || entry?.attachment_url || "";
  const url = resolveMediaUrl(rawUrl);
  if (!url) return null;

  const mimeType = typeof entry === "object" ? (entry?.mimeType || entry?.mime_type || "") : "";
  const mediaType = typeof entry === "object"
    ? (entry?.mediaType || entry?.media_type || entry?.type || "")
    : "";
  const normalizedType = mediaType === "image" || mediaType === "video"
    ? mediaType
    : inferReviewMediaType(mimeType, rawUrl);

  if (!normalizedType) return null;

  const fileSize =
    typeof entry === "object" && entry?.fileSize != null
      ? Number(entry.fileSize)
      : typeof entry === "object" && entry?.file_size != null
        ? Number(entry.file_size)
        : null;

  return {
    id: typeof entry === "object" ? (entry?.id ?? null) : null,
    url,
    mediaType: normalizedType,
    mimeType: mimeType || null,
    originalName:
      typeof entry === "object"
        ? (entry?.originalName || entry?.original_name || null)
        : null,
    fileSize: Number.isFinite(fileSize) ? fileSize : null,
    position:
      typeof entry === "object" && entry?.position != null
        ? Number(entry.position)
        : fallbackIndex,
  };
}

function parsePhotoArray(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function normalizeReviewMedia(media = [], legacyPhotos = []) {
  const entries = [];
  const seen = new Set();

  const pushEntry = (entry, index) => {
    const normalized = normalizeMediaEntry(entry, index);
    if (!normalized?.url || seen.has(normalized.url)) return;
    seen.add(normalized.url);
    entries.push(normalized);
  };

  (Array.isArray(media) ? media : []).forEach((entry, index) => pushEntry(entry, index));
  parsePhotoArray(legacyPhotos).forEach((entry, index) => pushEntry(entry, entries.length + index));

  return entries.sort((left, right) => Number(left.position || 0) - Number(right.position || 0));
}

export function toReviewAttachmentMeta(media, index = 0) {
  const normalized = normalizeMediaEntry(media, index);
  if (!normalized) return null;

  return {
    url: normalized.url,
    label:
      normalized.originalName ||
      (normalized.mediaType === "video" ? `Review video ${index + 1}` : `Review image ${index + 1}`),
    mimeType: normalized.mimeType || "",
    kind: normalized.mediaType,
    sizeLabel: normalized.fileSize ? formatAttachmentSize(normalized.fileSize) : "",
  };
}

export function validateReviewFiles(fileList) {
  const files = Array.from(fileList || []);
  if (files.length === 0) {
    return { ok: true, files: [], error: "" };
  }

  if (files.length > REVIEW_MAX_MEDIA_FILES) {
    return {
      ok: false,
      files: [],
      error: `You can upload up to ${REVIEW_MAX_MEDIA_FILES} review media files.`,
    };
  }

  let videoCount = 0;
  for (const file of files) {
    const mediaType = inferReviewMediaType(file?.type, file?.name);
    if (!mediaType) {
      return {
        ok: false,
        files: [],
        error: "Unsupported review media type. Use JPG, PNG, WebP, GIF, HEIC/HEIF, MP4, WEBM, or MOV.",
      };
    }

    if (file.size > REVIEW_MAX_FILE_SIZE_BYTES) {
      return {
        ok: false,
        files: [],
        error: "Each review media file must be 10 MB or smaller.",
      };
    }

    if (mediaType === "video") {
      videoCount += 1;
      if (videoCount > REVIEW_MAX_VIDEO_FILES) {
        return {
          ok: false,
          files: [],
          error: `You can upload up to ${REVIEW_MAX_VIDEO_FILES} review videos.`,
        };
      }
    }
  }

  return { ok: true, files, error: "" };
}
